import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Brain, Lock, User, Loader2, CheckCircle2, XCircle, ArrowRight, AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AcceptInvitation() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const [status, setStatus] = useState('validating'); // validating | valid | invalid | expired | used | activating | done
  const [invite, setInvite] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/invite/validate/${token}`);
      setInvite(res.data);
      setName(res.data.name || '');
      setStatus('valid');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Invalid invitation link';
      if (detail.includes('expired')) setStatus('expired');
      else if (detail.includes('already been used')) setStatus('used');
      else setStatus('invalid');
      setErrorMsg(detail);
    }
  };

  const handleActivate = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setStatus('activating');
    try {
      const res = await axios.post(`${API_URL}/api/invite/activate`, {
        token, password, name
      }, { withCredentials: true });
      setUser(res.data);
      localStorage.setItem('auth_token', res.data.token);
      setStatus('done');
      toast.success('Account created! Welcome to MindFlo.');
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Activation failed';
      toast.error(detail);
      setStatus('valid');
    }
  };

  // Loading state
  if (status === 'validating') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="invite-validating">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-muted-foreground font-mono text-sm">Validating invitation...</p>
        </div>
      </div>
    );
  }

  // Error states
  if (['invalid', 'expired', 'used'].includes(status)) {
    const config = {
      invalid: { icon: XCircle, title: 'Invalid Invitation', color: 'text-destructive' },
      expired: { icon: AlertTriangle, title: 'Invitation Expired', color: 'text-amber-400' },
      used: { icon: CheckCircle2, title: 'Already Activated', color: 'text-primary' },
    }[status];
    const Icon = config.icon;

    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid={`invite-${status}`}>
        <motion.div className="text-center max-w-md space-y-6" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
          <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-secondary/50`}>
            <Icon className={`h-8 w-8 ${config.color}`} />
          </div>
          <h2 className="text-2xl font-bold tracking-tight">{config.title}</h2>
          <p className="text-muted-foreground leading-relaxed">{errorMsg}</p>
          <div className="flex items-center justify-center gap-3">
            <Link to="/login">
              <Button variant="outline" data-testid="invite-error-login">Login</Button>
            </Link>
            <Link to="/request-access">
              <Button data-testid="invite-error-request">Request Access</Button>
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  // Success state
  if (status === 'done') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="invite-success">
        <motion.div className="text-center max-w-md space-y-6" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10">
            <CheckCircle2 className="h-8 w-8 text-primary" />
          </div>
          <h2 className="text-2xl font-bold tracking-tight">Welcome to MindFlo</h2>
          <p className="text-muted-foreground">Your account has been created. Redirecting to your dashboard...</p>
          <div className="h-1 w-32 mx-auto bg-secondary rounded-full overflow-hidden">
            <motion.div className="h-full bg-primary" initial={{ width: 0 }} animate={{ width: '100%' }} transition={{ duration: 2 }} />
          </div>
        </motion.div>
      </div>
    );
  }

  // Valid invitation - show activation form
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="invite-activate-page">
      <motion.div className="w-full max-w-md space-y-8" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        {/* Logo */}
        <div className="text-center space-y-2">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center">
              <Brain className="h-6 w-6 text-primary-foreground" strokeWidth={1.5} />
            </div>
            <span className="text-2xl font-semibold tracking-tight">MindFlo</span>
          </Link>
          <p className="text-muted-foreground text-sm">Set up your account to get started</p>
        </div>

        <Card className="bg-card border-border/50">
          <CardContent className="p-8">
            {invite && (
              <div className="mb-6 p-3 rounded-lg bg-primary/5 border border-primary/20 text-center">
                <p className="text-sm text-muted-foreground">Invitation for</p>
                <p className="font-medium" data-testid="invite-email">{invite.email}</p>
              </div>
            )}

            <form onSubmit={handleActivate} className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                  <Input
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Your full name"
                    className="pl-10"
                    required
                    data-testid="invite-name"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                  <Input
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Create a password (min 6 chars)"
                    className="pl-10"
                    minLength={6}
                    required
                    data-testid="invite-password"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    className="pl-10"
                    minLength={6}
                    required
                    data-testid="invite-confirm-password"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full glow-primary"
                disabled={status === 'activating'}
                data-testid="invite-activate-btn"
              >
                {status === 'activating' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    Create Account
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground">
          Already have an account?{' '}
          <Link to="/login" className="text-primary hover:underline font-medium">Login</Link>
        </p>
      </motion.div>
    </div>
  );
}
